import { useAuth0 } from "@auth0/auth0-react";
import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { Fragment, useContext, useState } from "react";
import { useSelector } from "react-redux";
import { getAppUser } from "../../../api/entity/appuser";
import { type auth0B2BUserType, userIsB2B } from "../../../api/entity/appuser_types";
import { Button } from "../../../components/common/Button";
import { Loader } from "../../../components/widgets/Loader";
import { custom_auth, externalProfileUrl } from "../../../context";
import { useApoAuth } from "../../../hooks";
import jssVariables from "../../../jss_setup";
import { TranslationContext } from "../../../umbilical_tools";
import { EditProfileModal } from "./EditProfileModal";
import { LogoAndColors } from "./LogoAndColors";
import { PushNotificationsCard } from "./PushNotificationsCard";
import type { SubagentData } from "./types";

const buttonStyles = {
  padding: `0 ${jssVariables.double_gap}`,
  minWidth: "7rem",
};

function Profile({ profile, getGuest }) {
  const [showEditModal, setShowEditModal] = useState(false);

  const allowEdit = useSelector((state) => state?.WebsiteMetadata?.metadata?.allow_profile_edit);

  const { user } = useAuth0() as { user: auth0B2BUserType | any };
  const { getIdTokenClaims } = useApoAuth();

  const isB2bUser = userIsB2B(user);

  const appUserDataQueryKey = ["appUser", user?.sub];
  const { data: appUser, isFetching: isAppUserFetching } = useQuery({
    queryKey: appUserDataQueryKey,
    queryFn: async () => {
      const token: { __raw: string } = (await getIdTokenClaims()) as { __raw: string };
      return getAppUser(user.sub, token.__raw);
    },
    enabled: !!user && isB2bUser,
    refetchOnWindowFocus: false,
  });

  const appUserData = appUser?.data;
  const isB2BUserLoading = isAppUserFetching;

  const user_picture = user?.picture;

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };
  const { logout, isB2B } = useApoAuth();

  const secondary_users = profile?.ext_alt_users?.users ?? [];

  // Check if user belongs to a subagent
  const isSubagent = appUserData?.entity_type === "subagent";

  // Mock subagent data (will be replaced with real API data later)
  const subagentData: SubagentData | null = isSubagent
    ? {
        logo_url: appUserData?.logo_url,
        entity_colors: appUserData?.entity_colors,
      }
    : null;

  return (
    <div className="Profile grid gap-3 p-3 lg:p-6 bg-white rounded-sm shadow-lg pb-12 lg:pb-12 lg:w-[80vw] xl:w-[80vw]">
      {showEditModal && (
        <EditProfileModal
          profile={profile}
          getGuest={getGuest}
          setShowEditModal={setShowEditModal}
        />
      )}
      <div className="text-xl font-medium">{t("profile")}</div>
      <div
        className={
          isSubagent
            ? "grid grid-cols-1 lg:grid-cols-3 gap-6 items-start"
            : "grid gap-3 lg:gap-6 lg:gap-x-12 lg:grid-cols-[max-content_1fr] lg:items-center lg:pl-6"
        }
      >
        {isSubagent ? (
          <div className="grid gap-3">
            <div className="grid place-items-center bg-[#d9d9d9] h-12 w-12 lg:h-16 lg:w-16 rounded-full">
              {user_picture ? (
                <img
                  src={user_picture}
                  alt=""
                  className="w-full h-full object-contain rounded-full"
                />
              ) : (
                <Icon icon="ic:baseline-person" className="text-xl text-white" />
              )}
            </div>
            <div className="grid gap-1 text-base">
              {secondary_users.length > 0 && (
                <Fragment>
                  <div>
                    <span>
                      <strong>Primary User</strong>
                    </span>
                  </div>
                  <div
                    className="border-b-2"
                    style={{ borderColor: jssVariables.colors.base_color }}
                  />
                </Fragment>
              )}
              {!isB2bUser && (
                <Fragment>
                  <div>
                    <span className="font-medium">{t("firstName")}: </span>
                    <span>{profile?.first_name ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t("lastName")}: </span>
                    <span>{profile?.last_name ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t("email")}: </span>
                    <span>{user?.email ?? profile?.email ?? "N/A"}</span>
                  </div>
                </Fragment>
              )}
              {isB2bUser && !isB2BUserLoading && !!appUserData ? (
                <Fragment>
                  <div>
                    <span className="font-medium">{t("firstName")}: </span>
                    <span>{appUserData.first_name ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t("lastName")}: </span>
                    <span>{appUserData.last_name ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t("email")}: </span>
                    <span>{appUserData.email ?? "N/A"}</span>
                  </div>
                </Fragment>
              ) : isB2bUser ? (
                <Loader />
              ) : null}
              {secondary_users.length > 0 && (
                <Fragment>
                  <div>
                    <span>
                      <strong>Secondary Users</strong>
                    </span>
                  </div>
                  <div
                    className="border-b-2"
                    style={{ borderColor: jssVariables.colors.base_color }}
                  />
                </Fragment>
              )}
              {secondary_users.map((usr, idx: number) => (
                <Fragment key={`${idx}-${usr?.email}`}>
                  <div key={idx}>
                    <span className="font-medium">{t("firstName")}: </span>
                    <span>{usr?.first_name ?? "N/A"}</span>
                  </div>
                  <div key={idx}>
                    <span className="font-medium">{t("lastName")}: </span>
                    <span>{usr?.last_name ?? "N/A"}</span>
                  </div>
                  <div key={idx}>
                    <span className="font-medium">{t("email")}: </span>
                    <span>{usr?.email ?? "N/A"}</span>
                  </div>
                  {idx > 0 && idx + 1 === secondary_users.length && (
                    <div
                      className="border-b-2"
                      style={{ borderColor: jssVariables.colors.base_color }}
                    />
                  )}
                </Fragment>
              ))}
              <div>
                {!custom_auth && (
                  <span className="font-medium" style={{ color: jssVariables.colors.base_color }}>
                    {(user?.email_verified ?? false)
                      ? `${t("verifiedEmail")}`
                      : `${t("unverifiedEmail")}`}
                  </span>
                )}
              </div>
            </div>
            <div />
            <div className="grid grid-cols-2 gap-3">
              {externalProfileUrl && (
                <div className="col-span-2">
                  <Button
                    text="Manage Subscription"
                    onClick={() => {
                      window.location.href = externalProfileUrl;
                    }}
                    customStyles={{ ButtonDefault: { ...buttonStyles } }}
                  />
                </div>
              )}
              {!isB2B && allowEdit && (
                <Button
                  text={t("edit")}
                  onClick={() => setShowEditModal(true)}
                  customStyles={{ ButtonDefault: { ...buttonStyles } }}
                />
              )}
              <Button
                text={t("logout")}
                onClick={() => {
                  if (typeof logout !== "function") return;
                  logout({ logoutParams: { returnTo: window.location.origin } });
                }}
                customStyles={{ ButtonDefault: { ...buttonStyles } }}
              />
            </div>
          </div>
        ) : (
          <>
            <div className="grid place-items-center bg-[#d9d9d9] h-12 w-12 lg:h-16 lg:w-16 rounded-full">
              {user_picture ? (
                <img
                  src={user_picture}
                  alt=""
                  className="w-full h-full object-contain rounded-full"
                />
              ) : (
                <Icon icon="ic:baseline-person" className="text-xl text-white" />
              )}
            </div>
            <div className="grid gap-1 text-base">
              {secondary_users.length > 0 && (
                <Fragment>
                  <div>
                    <span>
                      <strong>Primary User</strong>
                    </span>
                  </div>
                  <div
                    className="border-b-2"
                    style={{ borderColor: jssVariables.colors.base_color }}
                  />
                </Fragment>
              )}
              {!isB2bUser && (
                <Fragment>
                  <div>
                    <span className="font-medium">{t("firstName")}: </span>
                    <span>{profile?.first_name ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t("lastName")}: </span>
                    <span>{profile?.last_name ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t("email")}: </span>
                    <span>{user?.email ?? profile?.email ?? "N/A"}</span>
                  </div>
                </Fragment>
              )}
              {isB2bUser && !isB2BUserLoading && !!appUserData ? (
                <Fragment>
                  <div>
                    <span className="font-medium">{t("firstName")}: </span>
                    <span>{appUserData.first_name ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t("lastName")}: </span>
                    <span>{appUserData.last_name ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium">{t("email")}: </span>
                    <span>{appUserData.email ?? "N/A"}</span>
                  </div>
                </Fragment>
              ) : isB2bUser ? (
                <Loader />
              ) : null}
              {secondary_users.length > 0 && (
                <Fragment>
                  <div>
                    <span>
                      <strong>Secondary Users</strong>
                    </span>
                  </div>
                  <div
                    className="border-b-2"
                    style={{ borderColor: jssVariables.colors.base_color }}
                  />
                </Fragment>
              )}
              {secondary_users.map((usr, idx: number) => (
                <Fragment key={`${idx}-${usr?.email}`}>
                  <div key={idx}>
                    <span className="font-medium">{t("firstName")}: </span>
                    <span>{usr?.first_name ?? "N/A"}</span>
                  </div>
                  <div key={idx}>
                    <span className="font-medium">{t("lastName")}: </span>
                    <span>{usr?.last_name ?? "N/A"}</span>
                  </div>
                  <div key={idx}>
                    <span className="font-medium">{t("email")}: </span>
                    <span>{usr?.email ?? "N/A"}</span>
                  </div>
                  {idx > 0 && idx + 1 === secondary_users.length && (
                    <div
                      className="border-b-2"
                      style={{ borderColor: jssVariables.colors.base_color }}
                    />
                  )}
                </Fragment>
              ))}
              <div>
                {!custom_auth && (
                  <span className="font-medium" style={{ color: jssVariables.colors.base_color }}>
                    {(user?.email_verified ?? false)
                      ? `${t("verifiedEmail")}`
                      : `${t("unverifiedEmail")}`}
                  </span>
                )}
              </div>
            </div>
            <div />
            <div className="grid grid-cols-2 gap-3">
              {externalProfileUrl && (
                <div className="col-span-2">
                  <Button
                    text="Manage Subscription"
                    onClick={() => {
                      window.location.href = externalProfileUrl;
                    }}
                    customStyles={{ ButtonDefault: { ...buttonStyles } }}
                  />
                </div>
              )}
              {!isB2B && allowEdit && (
                <Button
                  text={t("edit")}
                  onClick={() => setShowEditModal(true)}
                  customStyles={{ ButtonDefault: { ...buttonStyles } }}
                />
              )}
              <Button
                text={t("logout")}
                onClick={() => {
                  if (typeof logout !== "function") return;
                  logout({ logoutParams: { returnTo: window.location.origin } });
                }}
                customStyles={{ ButtonDefault: { ...buttonStyles } }}
              />
            </div>
          </>
        )}
        {isSubagent && subagentData && (
          <>
            <LogoAndColors subagentData={subagentData} appUserDataQueryKey={appUserDataQueryKey} />
            <PushNotificationsCard />
          </>
        )}
      </div>
    </div>
  );
}
Profile.propTypes = {
  profile: PropTypes.object.isRequired,
  getGuest: PropTypes.func.isRequired,
};
export default Profile;
